import React, { useCallback, useMemo } from 'react';

/**
 * 全パターンの比率を計算して、100%以下のものを表示するコンポーネント
 * @param {Array} metrics - 利用可能なメトリクスのリスト
 * @param {Object} summary - サマリーデータ（メトリクス値）
 * @param {Array} daily_data - 日別データ（日ごとのメトリクス値）
 * @param {Object} targets - 目標値マップ
 * @param {Array} target_items - 目標項目リスト
 */
function RatioCalculator({ metrics = [], summary = {}, daily_data = [], targets = {}, target_items = [] }) {
  // 利用可能な数値メトリクス
  const numericMetrics = useMemo(() => {
    return metrics.filter(m => {
      const value = summary[m];
      return value !== null && value !== undefined && !isNaN(parseFloat(value));
    });
  }, [metrics, summary]);

  const resolveTargetValue = useCallback((metric) => {
    if (targets[metric] != null) {
      return Number(targets[metric] || 0);
    }

    const prefixed = `target_${metric}`;
    if (targets[prefixed] != null) {
      return Number(targets[prefixed] || 0);
    }

    const normalizedMetric = String(metric).toLowerCase();
    const item = target_items.find((row) => {
      const key = String(row.target_key || '').toLowerCase();
      const label = String(row.target_label || '').toLowerCase();
      return key === prefixed.toLowerCase() || key === normalizedMetric || label === normalizedMetric || label.includes(normalizedMetric);
    });

    if (item?.target_value == null) {
      return null;
    }

    return Number(item.target_value || 0);
  }, [targets, target_items]);

  // 全パターンの比率を計算（100%以下のもののみ）
  const ratioPatterns = useMemo(() => {
    const patterns = [];

    // 全組み合わせを計算
    for (let i = 0; i < numericMetrics.length; i++) {
      for (let j = 0; j < numericMetrics.length; j++) {
        if (i === j) continue; // 同じメトリクスは除外

        const numerator = numericMetrics[i];
        const denominator = numericMetrics[j];
        const numValue = parseFloat(summary[numerator] || 0);
        const denomValue = parseFloat(summary[denominator] || 0);
        const targetNumValue = resolveTargetValue(numerator);
        const targetDenomValue = resolveTargetValue(denominator);

        let targetRatio = null;
        if (
          targetNumValue !== null
          && targetDenomValue !== null
          && Number.isFinite(targetNumValue)
          && Number.isFinite(targetDenomValue)
          && targetDenomValue !== 0
        ) {
          targetRatio = parseFloat(((targetNumValue / targetDenomValue) * 100).toFixed(2));
        }

        // 分母が0でない場合のみ計算
        if (denomValue !== 0) {
          const ratio = (numValue / denomValue * 100);

          // 100%以下のもののみ
          if (ratio <= 100) {
            patterns.push({
              numerator,
              denominator,
              numValue,
              denomValue,
              ratio: parseFloat(ratio.toFixed(2)),
              targetRatio,
            });
          }
        }
      }
    }

    // 分母が大きい順、その次に分子が大きい順にソート
    return patterns.sort((a, b) => {
      if (b.denomValue !== a.denomValue) {
        return b.denomValue - a.denomValue;
      }
      return b.numValue - a.numValue;
    });
  }, [numericMetrics, summary, resolveTargetValue]);

  if (ratioPatterns.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
      <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
        比率計算（100%以下のパターン）
      </h3>

      {/* パターン一覧表 */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">分母</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">分子</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">分母値</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">分子値</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">比率 (%)</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">目標比率 (%)</th>
            </tr>
          </thead>
          <tbody>
            {ratioPatterns.map((pattern, idx) => {
              const isDenominatorChanged = idx > 0 && ratioPatterns[idx - 1].denominator !== pattern.denominator;
              const rowClass = isDenominatorChanged ? 'border-b border-t-2 border-t-black hover:bg-gray-50' : 'border-b hover:bg-gray-50';
              
              return (
                <tr key={idx} className={rowClass}>
                  <td className="px-3 py-2 text-gray-900 font-medium">{pattern.denominator}</td>
                  <td className="px-3 py-2 text-gray-900 font-medium">{pattern.numerator}</td>
                  <td className="px-3 py-2 text-right text-gray-700">
                    {pattern.denomValue.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700">
                    {pattern.numValue.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`inline-block px-2 py-1 rounded font-semibold ${
                        pattern.ratio >= 75
                          ? 'bg-green-100 text-green-800'
                          : pattern.ratio >= 60
                          ? 'bg-blue-100 text-blue-800'
                          : pattern.ratio >= 30
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {pattern.ratio}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 font-semibold">
                    {pattern.targetRatio === null ? '-' : `${pattern.targetRatio}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 凡例 */}
      <div className="mt-4 text-xs text-gray-600 flex gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 border border-green-800 rounded"></div>
          <span>75% 以上</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100 border border-blue-800 rounded"></div>
          <span>60% ～ 75%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 border border-gray-400 rounded"></div>
          <span>30% ～ 60%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-100 border border-red-800 rounded"></div>
          <span>30% 未満</span>
        </div>
      </div>
    </div>
  );
}

export default RatioCalculator;
